import type { RetentionType } from 'utils/retentionPolicies';
import { bool, nullable, num, object, oneOf, optional, str, unknownValue } from 'validation';

// Shared sub-object types + schemas for the node_definitions endpoints, plus the
// POST/PUT request body.
//
// The sub-objects are shared by IDENTITY: the backend serializes them from one
// place — the JSON_AGG subqueries in
// `modules/generic_functions/node_definitions/getNodeDefinitionByZoneAndName.js`
// — reused across the record responses. The flat record ENVELOPE, by contrast,
// is assembled separately by each GET/POST/PUT route handler (equal-by-
// coincidence), so it is redeclared per endpoint; see ../../README.md.

export type AutoSubmitMode = 'if_none_pending' | 'always';

export const AUTO_SUBMIT_MODES: readonly AutoSubmitMode[] = ['if_none_pending', 'always'];

// One output matcher: an ordered rule that searches a script node's output file after it runs. Fields mirror the backend
// table `node_definition_output_matchers` (and the GET JSON_BUILD_OBJECT). Only ever present on script-producing node types.
// `sort_order` is server-assigned (present on read, omitted on write — array position conveys order).
export type OutputMatcherFile = 'stdout.log' | 'stderr.log';
export type OutputMatcherProceed = 'continue' | 'abort';

const OUTPUT_MATCHER_FILES: readonly OutputMatcherFile[] = ['stdout.log', 'stderr.log'];
const OUTPUT_MATCHER_PROCEED: readonly OutputMatcherProceed[] = ['continue', 'abort'];

const RETENTION_TYPES: readonly RetentionType[] = ['minutes_to_keep', 'days_to_keep', 'months_to_keep', 'executions_to_keep'];

// One node-definition parameter. Shared between the record responses and the Save body: the backend round-trips the same
// field names. Loose at the type level — the backend owns the inter-field rules. `name` / `parameter_type` are the only
// always-present, non-null fields; the rest are optional/defaulted on write and nullable on read. `options` is opaque JSON.
// `order` is server-assigned (present on read, optional on write; array position conveys it). A `default_expression` of
// '******' on read means the value is a masked Password; sending that literal back keeps the stored secret (backend convention).
export interface Parameter {
  readonly name: string;
  readonly parameter_type: string;
  readonly default_expression?: string | null;
  readonly runtime?: boolean;
  readonly inout?: string;
  readonly options?: unknown;
  readonly mandatory?: boolean;
  readonly output_format?: string | null;
  readonly order?: number;
  readonly description?: string | null;
}

// A wait-event link at the DEFINITION level (full shape). Note the same key `wait_events` also appears per child-node
// placement with a NARROWER shape (only auto_submit/auto_clear) — that one lives inside the opaque `child_nodes` blob and is
// not this type. `path` / `scheduling_window` are "zone|name" strings; the backend resolves them.
export interface WaitEvent {
  readonly path: string | null;
  readonly auto_submit?: boolean;
  // Only meaningful when auto_submit is true; the backend rejects a mode without auto_submit.
  readonly auto_submit_mode?: AutoSubmitMode | null;
  readonly auto_clear?: boolean;
  readonly scheduling_window?: string | null;
  readonly precondition?: string | null;
}

// A raise-event link at the definition level. `path` is a "zone|name" string.
export interface RaiseEvent {
  readonly path: string | null;
  readonly raiser_comment?: string | null;
  readonly status?: string | null;
  readonly precondition?: string | null;
}

// One retention rule. The backend stores retention_policies as a pass-through JSONB array with no item validation; the shape
// is the contract enforced by the retention cron job (nap-backend cron-jobs/retention-policies-job.js + retentionPolicyRules.js).
// `status` is 'all' (wildcard) or a node-instance status — kept a plain string since the backend lowercases and groups freely.
export interface RetentionPolicy {
  readonly status: string;
  readonly retention_type: RetentionType;
  readonly value: number;
}

export interface OutputMatcher {
  readonly output_file: OutputMatcherFile;
  readonly search_string: string;
  readonly case_insensitive?: boolean;
  // Treat search_string as a Go RE2 regex (validated at save time on the backend).
  readonly use_regex?: boolean;
  // Overrides the agent-reported exit code; the derived status follows (0 -> completed, non-zero -> failed).
  readonly new_exit_code?: number | null;
  readonly proceed?: OutputMatcherProceed;
  // Server-assigned; present on read, omitted on write (order conveyed by array position).
  readonly sort_order?: number;
}

export const parameterSchema = object<Parameter>({
  name: str(),
  parameter_type: str(),
  default_expression: optional(nullable(str())),
  runtime: optional(bool()),
  inout: optional(str()),
  options: optional(unknownValue()),
  mandatory: optional(bool()),
  output_format: optional(nullable(str())),
  order: optional(num()),
  description: optional(nullable(str())),
});

export const waitEventSchema = object<WaitEvent>({
  path: nullable(str()),
  auto_submit: optional(bool()),
  auto_submit_mode: optional(nullable(oneOf(AUTO_SUBMIT_MODES, 'if_none_pending'))),
  auto_clear: optional(bool()),
  scheduling_window: optional(nullable(str())),
  precondition: optional(nullable(str())),
});

export const raiseEventSchema = object<RaiseEvent>({
  path: nullable(str()),
  raiser_comment: optional(nullable(str())),
  status: optional(nullable(str())),
  precondition: optional(nullable(str())),
});

export const outputMatcherSchema = object<OutputMatcher>({
  output_file: oneOf(OUTPUT_MATCHER_FILES, 'stdout.log'),
  search_string: str(),
  case_insensitive: optional(bool()),
  use_regex: optional(bool()),
  new_exit_code: optional(nullable(num())),
  proceed: optional(oneOf(OUTPUT_MATCHER_PROCEED, 'continue')),
  sort_order: optional(num()),
});

export const retentionPolicySchema = object<RetentionPolicy>({
  status: str(),
  retention_type: oneOf(RETENTION_TYPES, 'days_to_keep'),
  value: num(),
});

// POST/PUT request body. Source of truth for what the backend accepts: nap-backend
//   routes/node_definitions.js POST / PUT handlers        — body destructure + folder/queue/scheduling-window/runtime-limit resolution.
//   modules/generic_functions/node_definitions/upsertNodeDefinition.js — the field reads and per-collection validation.
//
// Permissive on purpose: fields the backend requires are typed `T | null`, not strictly `T` — the server owns the validity
// rules and returns 400 on a bad body, which the UI surfaces. A too-strict client type would block valid sends whenever the
// backend's rules loosen. The opaque members (`config`, `handles`, `child_nodes`, `child_edges`) are forwarded exactly as
// held; the strict collections reuse the wire sub-object types above (same field names round-trip). The FE authors this body,
// so it is never validated here.
export interface SaveNodeDefinition {
  readonly name: string | null;
  readonly description: string | null;
  readonly type: string | null;
  readonly sub_type: string | null;
  // The backend resolves `zone_name` -> zone_id (and PUT also infers it from the URL).
  readonly zone_name: string | null;
  // Folder PATH string (the backend resolves it to an id); `folder_id` is never sent.
  readonly folder_name: string | null;
  // "queueZone|queueName" string.
  readonly default_queue_path: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
  // "zone|lock" tag strings.
  readonly locks: readonly string[];
  readonly retention_policies: readonly RetentionPolicy[];
  // Ordered; sending `[]` clears all matchers, omitting the key would leave stored matchers unchanged — the model always
  // sends the authoritative array.
  readonly output_matchers: readonly OutputMatcher[];
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  // Ordered exit codes to restrict auto-restart to (negatives allowed); empty restarts on any failure. The backend dedupes
  // preserving order and coerces integer-like strings, so the client sends what the user entered.
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  // HH:MM:SS (or a "{{var}}" / "={{var}}*1000" expression); the backend converts to the ms columns.
  readonly runtime_limit_min_hhmmss: string | null;
  readonly runtime_limit_max_hhmmss: string | null;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  readonly documentation: string | null;
  // Optimistic-concurrency token (PUT only): the loaded `version`. Omitted on create; the backend skips the check when absent.
  readonly if_match_version?: number;
  // PUT only: parameter names deleted in this save, so the backend strips their mappings from referencing flow nodes.
  readonly remove_parameter_names?: readonly string[];
}
